import { useCallback, useRef } from 'react'

type DragGhostOptions = {
  width: number
  height: number
  offsetX?: number
  offsetY?: number
}

export const useDragGhost = () => {
  const dragGhostRef = useRef<HTMLElement | null>(null)

  const createDragGhost = useCallback(
    (
      element: HTMLElement,
      options: DragGhostOptions,
      initialPosition?: { x: number; y: number }
    ) => {
      const {
        width,
        height,
        offsetX = width / 2,
        offsetY = height / 2
      } = options

      if (
        dragGhostRef.current &&
        document.body.contains(dragGhostRef.current)
      ) {
        document.body.removeChild(dragGhostRef.current)
      }

      const dragGhost = element.cloneNode(true) as HTMLElement
      dragGhost.style.position = 'fixed'
      dragGhost.style.pointerEvents = 'none'
      dragGhost.style.zIndex = '10000'
      dragGhost.style.opacity = '0.8'
      dragGhost.style.transform = 'scale(1.02)'
      dragGhost.style.boxShadow = '0 14px 26px rgba(184, 168, 168, 0.2)'
      dragGhost.style.width = `${width}px`
      dragGhost.style.height = `${height}px`

      if (initialPosition) {
        dragGhost.style.left = `${initialPosition.x - offsetX}px`
        dragGhost.style.top = `${initialPosition.y - offsetY}px`
      } else {
        dragGhost.style.top = '-9999px'
        dragGhost.style.left = '-9999px'
      }

      document.body.appendChild(dragGhost)
      dragGhostRef.current = dragGhost

      return dragGhost
    },
    []
  )

  const updateDragGhostPosition = useCallback(
    (x: number, y: number, offsetX: number, offsetY: number) => {
      if (!dragGhostRef.current) return

      dragGhostRef.current.style.left = `${x - offsetX}px`
      dragGhostRef.current.style.top = `${y - offsetY}px`
    },
    []
  )

  const removeDragGhost = useCallback(() => {
    if (dragGhostRef.current && document.body.contains(dragGhostRef.current)) {
      document.body.removeChild(dragGhostRef.current)
    }
    dragGhostRef.current = null
  }, [])

  return {
    createDragGhost,
    updateDragGhostPosition,
    removeDragGhost,
    dragGhost: dragGhostRef.current
  }
}
